<script lang="ts">
    import RewardsLink from "@apps/rewards/components/RewardsLink.svelte";
    import RewardsPoints from "@apps/rewards/components/RewardsPoints.svelte";
    import { REWARDS_REWARD_TYPES } from "@apps/rewards/constants/rewardsRewardTypes";
    import type {
        DiscountRewardEntity,
        FreeProductRewardEntity,
        GiftCardRewardEntity,
        PointsRewardEntity,
    } from "@apps/rewards/entities/entities";
    import type RewardsAmountFormatter from "@apps/rewards/lib/RewardsAmountFormatter/RewardsAmountFormatter";
    import type RewardsCurrencyFormatter from "@apps/rewards/lib/RewardsCurrencyFormatter";
    import type { TranslationNamespaces } from "@apps/rewards/types";
    import type { URLsBuilder } from "@modules/common_module/URLsBuilder/URLsBuilder";
    import type { GwI18n } from "@modules/i18n/GwI18n/GwI18n";
    import type HTMLSanitizerInterface from "@modules/money/HTMLSanitizer/HTMLSanitizerInterface";

    import T from "@components/T.svelte";

    import DiscountReward from "./DiscountReward.svelte";
    import FreeProductReward from "./FreeProductReward.svelte";
    import GiftCardReward from "./GiftCardReward.svelte";

    export let reward:
        | PointsRewardEntity
        | Omit<DiscountRewardEntity, "discountCode">
        | Omit<GiftCardRewardEntity, "code" | "balance">
        | FreeProductRewardEntity;
    export let amountFormatter: RewardsAmountFormatter;
    export let currencyFormatter: RewardsCurrencyFormatter;
    export let i18n: GwI18n;
    export let namespace: TranslationNamespaces;
    export let pointsNamespace: TranslationNamespaces;
    export let urlsBuilder: URLsBuilder;
    export let sanitizer: HTMLSanitizerInterface;

    const { points, discount, giftCard, freeProduct } = REWARDS_REWARD_TYPES;
    const freeProductMaxDiscount = 100;
</script>

{#if reward.type === points}
    <RewardsPoints
        points={reward.points}
        {amountFormatter}
        {i18n}
        {pointsNamespace}
    />
{/if}

{#if reward.type === discount}
    <DiscountReward {reward} {currencyFormatter} {sanitizer}>
        <T
            slot="fixed_discount"
            let:reward={{ formattedDiscount }}
            {namespace}
            translationKey="fixed_discount_reward"
            translationOptions={{
                discount: formattedDiscount,
            }}
        />
        <T
            slot="percentage_discount"
            let:reward={{ formattedDiscount }}
            {namespace}
            translationKey="percentage_discount_reward"
            translationOptions={{
                discount: formattedDiscount,
            }}
        />
        <T
            slot="free_shipping"
            {namespace}
            translationKey="free_shipping_reward"
        />
    </DiscountReward>
{/if}

{#if reward.type === giftCard}
    <GiftCardReward
        let:reward={{ formattedAmount }}
        {reward}
        {currencyFormatter}
        {sanitizer}
    >
        <T
            {namespace}
            translationKey="gift_card_reward"
            translationOptions={{
                amount: formattedAmount,
            }}
        />
    </GiftCardReward>
{/if}

{#if reward.type === freeProduct}
    <FreeProductReward let:reward={{ reward, product, variant }} {reward}>
        <RewardsLink
            href={urlsBuilder.createProductUrl(product.handle, variant.id)}
        >
            {#if reward.discountValue === freeProductMaxDiscount}
                <T
                    {namespace}
                    translationKey="free_product_reward_max_discount"
                />
            {:else}
                <T
                    {namespace}
                    translationKey="free_product_reward_variable_discount"
                    translationOptions={{
                        discount: reward.discountValue,
                    }}
                />
            {/if}
        </RewardsLink>
    </FreeProductReward>
{/if}
